import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { apiFetch } from "../../utils/apiFetch";
import { BRAND } from "../risikoanalyseStep/tokens";
import type { CompanyNetworkFirma } from "../../services/companyNetworkService";
import { anlageDatenAus, type AnlagePosition } from "./anlagenverzeichnis";
import {
  KENNZAHL_MERKMAL,
  OHNE_KENNZAHL,
  besitzFuer,
  kennzahlFuer,
  kennzahlLabel,
  kennzahlZeilen,
  summeFuer,
  type Besitz,
  type KennzahlZeile,
} from "./vermoegensKennzahlen";
import { InfoTipp } from "./InfoTipp";
import { Icons } from "./riskIcons";
import { wvAusJahren, type WvJahrRoh } from "./warenVorraete";

/**
 * Vermögens-Matrix „Kennzahl | Wert (Eigentum / geleast …) | Wo | Wem | Versichert" über Risikoobjekte
 * mehrerer Firmen (Ebenen: Dieses Unternehmen / Unternehmensverbund). Die frühere dritte
 * Ebene „Kundenverbindung" ist bewusst entfallen — Betriebsvermögen wird je
 * Rechtsträger bzw. Verbund betrachtet, nicht über die Kundenverbindung
 * hinweg (Meeting 24.07.2026).
 * „Versichert" = Deckungsprüfung: Vertrag ja/nein, VN, Mitversicherte —
 * Risiken ohne Vertrag werden automatisch hervorgehoben. Die Zeilen sind die Kennzahlen der
 * Versicherungssicht (`vermoegensKennzahlen.ts`), nicht die Konten der Buchhaltung.
 * Fachkonzept:
 * docs/plan-risiken-verbund-ansichten-vorplanung.md (Flipchart-Matrix 23.07.2026).
 */

interface MatrixContact {
  id: number;
  lastname?: string | null;
  firstName?: string | null;
  displayName?: string | null;
}

interface MatrixContract {
  id: number;
  contractNbr?: string | null;
  branch?: string | null;
  contactId?: number | null;
  contact?: MatrixContact | null;
  gesellschaft?: { id?: number; name?: string | null } | null;
}

interface MatrixRisk {
  id: number;
  riskType?: string | null;
  name?: string | null;
  riskObjectIdentifier?: string | null;
  contactID?: number | null;
  contractID?: number | null;
  // decimals kommen vom pg-Treiber als String
  versicherungssumme?: number | string | null;
  contract?: MatrixContract | null;
  contacts?: MatrixContact[] | null;
  directContact?: MatrixContact | null;
  characteristics?:
    | { nameBezeichnung?: string | null; riskCharacteristicValue?: string | null }[]
    | null;
}

/** Scope-Ebene der Firmenauswahl. Der Umschalter selbst lebt in ContactRisksPanel. */
export type Ebene = "firma" | "verbund";

const ORT_KEYS = ["Risikoort", "Anschrift", "Risikoanschrift"];
const VERMART_KEY = "Vermögensart";
const EIGART_KEY = "Eigentumsart";

/**
 * Fremdes Eigentum im Betrieb bekommt eigene Zeilen statt einer Sammelzeile
 * „Eigentum von Mitarbeitern und Kunden": Mitarbeiter- und Kundeneigentum sind
 * versicherungstechnisch getrennt zu betrachten (eigene Entschädigungsgrenzen
 * in der Inhaltsversicherung). Erkannt wird beides an der Eigentumsart; die
 * Altdaten tragen die Unterscheidung nur dort, während die Vermögensart noch
 * die zusammengefasste Bezeichnung trägt.
 */
const FREMDEIGENTUM_MITARBEITER = "Eigentum von Mitarbeitern";
const FREMDEIGENTUM_KUNDEN = "Eigentum von Kunden";

const vermartLabel = (vermart: string, eigart: string | null): string => {
  const e = (eigart ?? "").toLowerCase();
  const v = vermart.toLowerCase();
  if (e.includes("mitarbeiter") || (v.includes("mitarbeiter") && !v.includes("kunden")))
    return FREMDEIGENTUM_MITARBEITER;
  if (e.includes("kunden") || v.includes("kunden")) return FREMDEIGENTUM_KUNDEN;
  return vermart;
};

/** Erklärungen der beiden „Davon"-Spalten (Blase am ⓘ). */
const ERKLAERUNG_EIGENTUM =
  "Gehört dem Unternehmen selbst — einschließlich Mietereinbauten in gemieteten Räumen.";
const ERKLAERUNG_BESITZ =
  "Geleaste, gemietete und zur Verfügung gestellte Betriebseinrichtung sowie Mietkauf.";

export const EBENEN: { id: Ebene; label: string; hint: string }[] = [
  { id: "firma", label: "Dieses Unternehmen", hint: "Nur der geöffnete Kontakt." },
  {
    id: "verbund",
    label: "Unternehmensverbund",
    hint: "Alle über Tochter-/Schwester-/Beteiligungs-Verbindungen erreichbaren Firmen.",
  },
];

const eur = (v: number) =>
  `${Math.round(v).toLocaleString("de-DE")} €`;

const charOf = (risk: MatrixRisk, keys: string[]): string | null => {
  for (const key of keys) {
    const hit = risk.characteristics?.find(
      (c) => (c.nameBezeichnung ?? "").trim().toLowerCase() === key.toLowerCase(),
    );
    const val = hit?.riskCharacteristicValue?.trim();
    if (val) return val;
  }
  return null;
};

const contactLabel = (c?: MatrixContact | null): string => {
  if (!c) return "—";
  if (c.displayName) return c.displayName;
  return [c.firstName, c.lastname].filter(Boolean).join(" ") || `Kontakt ${c.id}`;
};

const wertOf = (risk: MatrixRisk): number => {
  const n = Number(risk.versicherungssumme);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Vermögensblock (Meeting 24.07.2026). Gebäude, Photovoltaik und Kfz gehören
 * NICHT in die Betriebsvermögens-Summe: sie haben eigene Datenquellen
 * (Immobilienakte bzw. Kfz-Daten) und eigene Verträge — mitsummiert würden sie
 * die Versicherungssumme für Inhalt/BGA/Maschinen verfälschen.
 */
type Block = "betrieb" | "gebaeude" | "pv" | "kfz" | "umwelt";

/**
 * Ordnet ein Risiko einem Block zu. Reihenfolge zählt: Photovoltaik wird VOR
 * Gebäude geprüft, weil eine Dachanlage häufig als Immobilien-Risiko erfasst
 * ist und sonst im Gebäudeblock verschwinden würde.
 */
const blockOf = (riskType: string, vermart: string): Block => {
  const t = `${riskType} ${vermart}`.toLowerCase();
  // Die Umwelt-Analyse ist ein Fragebogen, kein Sachwert — sie steht in den
  // Objektmappen und darf hier nicht als „Keiner Kennzahl zugeordnet" auftauchen.
  if (riskType.toLowerCase() === "umwelt") return "umwelt";
  if (t.includes("photovolt") || t.includes("pv-anlage") || t.includes("solar")) return "pv";
  if (t.includes("kfz") || t.includes("fahrzeug")) return "kfz";
  if (t.includes("immo") || t.includes("gebäude") || t.includes("gebaeude") || t.includes("grundstück"))
    return "gebaeude";
  return "betrieb";
};

interface RiskRow {
  risk: MatrixRisk;
  ownerId: number | null;
  ownerName: string;
  ort: string | null;
  /** Anzeige-Kategorie der „Was"-Zeile (Fremdeigentum aufgetrennt). */
  vermart: string;
  eigart: string | null;
  wert: number;
  versichert: boolean;
  vnName: string | null;
  sparte: string | null;
  contractNbr: string | null;
  mitversicherte: string[];
  isKfz: boolean;
  block: Block;
  /** Buchhaltungsdaten der Position — leer, wenn kein Anlageverzeichnis vorliegt. */
  anlage: AnlagePosition;
  /** Anzeigenummer im Verzeichnis: die Kennung, sonst die technische id. */
  nr: string;
  /** Blatt-id der Versicherungs-Kennzahl (`vermoegensKennzahlen.ts`). */
  kennzahl: string;
  /** Spalte „Im Eigentum" oder „Geleast, gemietet, …". */
  besitz: Besitz;
  /**
   * „kpi" = kein Risikoobjekt, sondern ein Vorratswert aus den Kennzahlen des
   * Kunden (früherer Abschnitt „Umlaufvermögen"). Nicht anklickbar und nicht
   * Teil der Deckungsprüfung — ein Vorratswert hat keinen eigenen Vertrag.
   */
  quelle?: "kpi";
}

const toRow = (risk: MatrixRisk, firmName: (id: number) => string | null): RiskRow => {
  const ownerId = risk.contactID ?? risk.contacts?.[0]?.id ?? risk.directContact?.id ?? null;
  const ownerContact =
    risk.contacts?.find((c) => c.id === ownerId) ?? risk.directContact ?? risk.contacts?.[0];
  const isKfz = (risk.riskType ?? "").toUpperCase() === "KFZ";
  const vermartRaw = charOf(risk, [VERMART_KEY]) ?? (isKfz ? "Kfz" : risk.riskType || "Sonstiges");
  const eigart = charOf(risk, [EIGART_KEY]);
  const versichert = !!(risk.contract || risk.contractID);
  const vermart = vermartLabel(vermartRaw, eigart);
  const kennzahl = kennzahlFuer(charOf(risk, [KENNZAHL_MERKMAL]), vermart, eigart);
  // Bauten auf fremdem Grund laufen im Import als Immobilie. Für die
  // Versicherung sind sie eingebrachte Sachen (Mietereinbauten) und gehören in
  // die Summe des Betriebsvermögens — nicht zu den Gebäuden des Eigentümers.
  const block = kennzahl === "fremdgrund" ? "betrieb" : blockOf(risk.riskType ?? "", vermartRaw);
  return {
    risk,
    ownerId,
    ownerName:
      (ownerId != null ? firmName(ownerId) : null) ?? contactLabel(ownerContact),
    ort: isKfz ? null : charOf(risk, ORT_KEYS),
    vermart,
    eigart,
    wert: wertOf(risk),
    versichert,
    vnName: risk.contract ? contactLabel(risk.contract.contact) : null,
    sparte: risk.contract?.branch ?? null,
    contractNbr: risk.contract?.contractNbr ?? null,
    mitversicherte: (risk.contacts ?? [])
      .filter((c) => c.id !== ownerId)
      .map((c) => contactLabel(c)),
    isKfz,
    block,
    anlage: anlageDatenAus(risk.characteristics),
    nr: risk.riskObjectIdentifier ?? String(risk.id),
    kennzahl,
    besitz: besitzFuer(kennzahl, eigart),
  };
};

/** Antwort von `risk-kpi/by-contacts` — je Firma die Jahresreihe. */
interface KpiJahreProFirma {
  contactId: number;
  years?: WvJahrRoh[] | null;
}

/**
 * Vorratswerte aus den Kennzahlen als Matrix-Zeilen. Früher standen sie im
 * eigenen Abschnitt „Umlaufvermögen"; seit Waren und Vorräte eine Kennzahl der
 * Matrix sind, erscheinen sie hier — sonst verschwänden erfasste Beträge mit
 * dem Abschnitt. Die Aufteilung der Kennzahlen kennt nur drei Posten; was die
 * genannte Gesamtsumme darüber hinaus trägt, steht unter „Sonstige".
 *
 * Nur für Firmen OHNE Vorrats-Risikoobjekte: Sind Vorräte als Positionen
 * erfasst, wären die Kennzahlen eine zweite Zählung desselben Bestands.
 */
const kpiVorratsZeilen = (
  firmaId: number,
  firmaName: string,
  jahre: WvJahrRoh[],
): RiskRow[] => {
  const wv = wvAusJahren(jahre);
  if (wv.jahr == null) return [];
  const teile: [string, string, number | null][] = [
    ["wv_rhb", "Unverarbeitete Erzeugnisse (Roh-, Hilfs- und Betriebsstoffe)", wv.unverarbeitet],
    ["wv_erz", "Halbfertige Erzeugnisse", wv.halbfertig],
    ["wv_erz", "Fertige Erzeugnisse und Waren", wv.fertig],
  ];
  const teilsumme = teile.reduce((a, [, , v]) => a + (v ?? 0), 0);
  if (wv.gesamt != null && wv.gesamt > teilsumme)
    teile.push(["wv_sonst", "Rest der genannten Gesamtsumme", wv.gesamt - teilsumme]);
  return teile
    .filter(([, , v]) => v != null && v > 0)
    .map(([kennzahl, name, v], i) => ({
      risk: { id: -(firmaId * 10 + i + 1), name: `${name} · Kennzahlen ${wv.jahr}` },
      ownerId: firmaId,
      ownerName: firmaName,
      ort: null,
      vermart: "Waren / Vorräte",
      eigart: null,
      wert: v as number,
      versichert: false,
      vnName: null,
      sparte: null,
      contractNbr: null,
      mitversicherte: [],
      isKfz: false,
      block: "betrieb" as const,
      anlage: anlageDatenAus(null),
      nr: `KZ ${wv.jahr}`,
      kennzahl,
      besitz: besitzFuer(kennzahl, null),
      quelle: "kpi" as const,
    }));
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
  },
  scopeBar: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: "#faf8fb",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
  },
  scopeRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "8px",
    rowGap: "6px",
  },
  scopeLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: BRAND.muted,
    minWidth: "56px",
  },
  seg: {
    display: "inline-flex",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("2px"),
    columnGap: "2px",
  },
  segBtn: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    ...shorthands.borderRadius("999px"),
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    color: BRAND.muted,
    cursor: "pointer",
  },
  segBtnOn: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontWeight: 700,
  },
  hint: { fontSize: "0.75rem", color: BRAND.muted },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.white,
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "11px",
    paddingRight: "11px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    fontWeight: 600,
    color: BRAND.muted,
    cursor: "pointer",
  },
  chipOn: {
    backgroundColor: "#f0e4ee",
    ...shorthands.borderColor(BRAND.purple),
    color: BRAND.ink,
  },
  chipCount: {
    fontSize: "0.7rem",
    backgroundColor: "#f5eef4",
    color: BRAND.purple,
    ...shorthands.borderRadius("999px"),
    paddingLeft: "6px",
    paddingRight: "6px",
  },
  chipDanger: {
    backgroundColor: "#faecec",
    ...shorthands.borderColor("#c23b3b"),
    color: "#c23b3b",
  },
  select: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    fontWeight: 600,
    color: BRAND.muted,
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    backgroundColor: BRAND.white,
  },
  tableWrap: {
    overflowX: "auto",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("12px"),
    backgroundColor: BRAND.white,
    boxShadow: "0 2px 6px rgba(45,20,43,0.04)",
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
    fontSize: "0.82rem",
  },
  th: {
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
    textAlign: "right",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: BRAND.border,
    whiteSpace: "nowrap",
  },
  thGroup: {
    textAlign: "center",
    backgroundColor: "#f5eef4",
    color: BRAND.purple,
    letterSpacing: "0.1em",
  },
  thLeft: { textAlign: "left" },
  groupBorder: {
    borderLeftWidth: "2px",
    borderLeftStyle: "solid",
    borderLeftColor: BRAND.border,
  },
  td: {
    textAlign: "right",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: BRAND.borderDashed,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  tdLeft: { textAlign: "left", fontWeight: 600 },
  cellBtn: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    fontFamily: BRAND.fontPrimary,
    fontSize: "inherit",
    color: "inherit",
    cursor: "pointer",
    ...shorthands.padding("0"),
    fontVariantNumeric: "tabular-nums",
  },
  cellHas: {
    color: BRAND.purple,
    fontWeight: 700,
    textDecorationLine: "underline",
    textDecorationColor: "#e6d3e3",
    textUnderlineOffset: "3px",
  },
  cellZero: { color: "#d6cfd6" },
  totalRow: {
    backgroundColor: "#fdf8ee",
    fontWeight: 800,
    borderTopWidth: "2px",
    borderTopStyle: "solid",
    borderTopColor: BRAND.gold,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    fontSize: "0.68rem",
    fontWeight: 700,
    ...shorthands.borderRadius("6px"),
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "7px",
    paddingRight: "7px",
    whiteSpace: "nowrap",
  },
  badgeOk: { backgroundColor: "#e8f3ee", color: BRAND.pillarGreen },
  badgeDanger: { backgroundColor: "#faecec", color: "#c23b3b" },
  badgeNeutral: {
    backgroundColor: "#f5f2f5",
    color: BRAND.muted,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  rowDanger: { backgroundColor: "#fdf5f5" },
  /** Versicherungsnehmer — darf umbrechen, mehrere VN je Vermögensart. */
  vnList: {
    fontSize: "0.78rem",
    color: BRAND.ink,
    whiteSpace: "normal",
    display: "inline-block",
    maxWidth: "220px",
  },
  drill: {
    ...shorthands.border("1px", "solid", "#e6d3e3"),
    ...shorthands.borderRadius("12px"),
    backgroundColor: BRAND.white,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    boxShadow: "0 2px 6px rgba(45,20,43,0.04)",
  },
  drillHead: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "8px",
    marginBottom: "6px",
    fontWeight: 700,
    fontSize: "0.85rem",
  },
  drillClose: {
    marginLeft: "auto",
    ...shorthands.border("0"),
    backgroundColor: "#f5f2f5",
    ...shorthands.borderRadius("6px"),
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.75rem",
    fontWeight: 700,
    color: BRAND.muted,
    cursor: "pointer",
  },
  drillItem: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    columnGap: "10px",
    rowGap: "2px",
    paddingTop: "5px",
    paddingBottom: "5px",
    borderBottomWidth: "1px",
    borderBottomStyle: "dashed",
    borderBottomColor: BRAND.borderDashed,
    fontSize: "0.8rem",
    cursor: "pointer",
  },
  muted: { color: BRAND.muted },

  /* Kennzahlen-Baum der Matrix */
  kzGruppe: { backgroundColor: "#faf6fa" },
  kzUntergruppe: { fontWeight: 700 },
  kzBlatt: { fontWeight: 500 },
  /** Auf-/Zuklapp-Pfeil vor einer Gruppe; Blätter bekommen denselben Platz leer. */
  kzToggle: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    marginRight: "4px",
    padding: 0,
    verticalAlign: "middle",
    color: BRAND.muted,
    backgroundColor: "transparent",
    cursor: "pointer",
    ...shorthands.border("0"),
    transitionProperty: "transform",
    transitionDuration: "140ms",
    ":hover": { color: BRAND.purple },
  },
  kzToggleOffen: { transform: "rotate(90deg)" },
  kzTogglePlatz: { display: "inline-block", width: "16px", marginRight: "4px" },

  /* Gruppen-Zwischenzeile im Anlagenverzeichnis (Cluster je Vermögensbereich) */
  groupRow: {
    backgroundColor: "#f3eef5",
    fontWeight: 700,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  /* Objektblöcke (Gebäude/PV/Kfz) — bewusst NEBEN der Betriebsvermögens-Summe */
  bloecke: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  },
  blockCard: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("10px"),
  },
  blockLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  blockValue: {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  blockMeta: {
    fontSize: "0.75rem",
    color: BRAND.muted,
  },
  blockWarn: { color: "#c23b3b", fontWeight: 600 },
  blockLeer: { opacity: 0.55 },

  empty: {
    textAlign: "center",
    color: BRAND.muted,
    paddingTop: "18px",
    paddingBottom: "18px",
    fontSize: "0.85rem",
  },
});

interface VermoegensMatrixProps {
  /** Firmen des aktuellen Scopes (Ebene „Dieses Unternehmen" oder „Unternehmensverbund"). */
  scopeFirmen: CompanyNetworkFirma[];
  /** Davon ausgewählt — aufsteigend sortiert, nie leer. */
  selectedIds: number[];
  /**
   * Meldet die Zahl der Positionen nach oben. Der Reiter entscheidet damit, ob
   * der Abschnitt offen oder zugeklappt startet — laden würde er sie sonst ein
   * zweites Mal, nur um zu wissen, ob überhaupt etwas da ist.
   */
  onBefund?: (anzahl: number) => void;
}

export function VermoegensMatrix({ scopeFirmen, selectedIds, onBefund }: VermoegensMatrixProps) {
  const styles = useStyles();
  const navigate = useNavigate();

  const [ortFilter, setOrtFilter] = useState<string>("alle");
  const [nurOhneVertrag, setNurOhneVertrag] = useState(false);
  const [drill, setDrill] = useState<string | null>(null);
  /** Eingeklappte Kennzahl-Gruppen — die Summe der Gruppe bleibt sichtbar. */
  // Die dritte Ebene (Elektronik, Maschinen stationär / mobil) startet
  // zugeklappt — ihre Summen stehen trotzdem da (Vorgabe 12.10.2026).
  const [zugeklappt, setZugeklappt] = useState<Set<string>>(
    () => new Set(["el", "ms", "m_mobil"]),
  );
  const umklappen = (id: string) =>
    setZugeklappt((prev) => {
      const neu = new Set(prev);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });

  const [risks, setRisks] = useState<MatrixRisk[]>([]);
  const [loading, setLoading] = useState(false);

  // Scope-Wechsel von oben → Drill-Down und Ortsfilter zurücksetzen, sonst
  // filtert die Ansicht auf einen Ort/eine Vermögensart, die es im neuen
  // Scope gar nicht gibt (leere Matrix ohne erkennbaren Grund).
  const scopeKey = selectedIds.join(",");
  useEffect(() => {
    setDrill(null);
    setOrtFilter("alle");
  }, [scopeKey]);

  useEffect(() => {
    if (selectedIds.length === 0) {
      setRisks([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiFetch<MatrixRisk[]>(`risk-objects/by-contacts`, {
      params: { ids: selectedIds.join(",") },
    })
      .then(({ data }) => {
        if (!cancelled) setRisks(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setRisks([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedIds.join(",")]);

  // Vorratswerte aus den Kennzahlen (siehe `kpiVorratsZeilen`). Ein Fehler
  // hier lässt die Matrix stehen — dann fehlen eben nur diese Zeilen.
  const [kpiJahre, setKpiJahre] = useState<KpiJahreProFirma[]>([]);
  useEffect(() => {
    if (selectedIds.length === 0) {
      setKpiJahre([]);
      return;
    }
    let cancelled = false;
    apiFetch<KpiJahreProFirma[]>("risk-kpi/by-contacts", {
      params: { ids: selectedIds.join(",") },
    })
      .then(({ data }) => {
        if (!cancelled) setKpiJahre(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setKpiJahre([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedIds.join(",")]);

  const firmNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const f of scopeFirmen) if (f.name) map.set(f.id, f.name);
    return map;
  }, [scopeFirmen]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const allRows = useMemo(() => {
    const objekte = risks
      .map((r) => toRow(r, (id) => firmNameById.get(id) ?? null))
      // nur Objekte gewählter Firmen (by-contacts matcht auch über Vertrags-VN)
      .filter((row) => row.ownerId == null || selectedSet.has(row.ownerId));
    const mitVorratsObjekten = new Set(
      objekte.filter((r) => r.kennzahl.startsWith("wv_")).map((r) => r.ownerId),
    );
    const ausKennzahlen = kpiJahre
      .filter((e) => selectedSet.has(e.contactId) && !mitVorratsObjekten.has(e.contactId))
      .flatMap((e) =>
        kpiVorratsZeilen(
          e.contactId,
          firmNameById.get(e.contactId) ?? `Kontakt ${e.contactId}`,
          e.years ?? [],
        ),
      );
    return [...objekte, ...ausKennzahlen];
  }, [risks, kpiJahre, firmNameById, selectedSet]);

  // Nur Orte des Betriebsvermögens: ein Ort, an dem ausschließlich ein Gebäude
  // liegt, würde die Matrix sonst komplett leer filtern.
  const orte = useMemo(
    () =>
      Array.from(
        new Set(
          allRows
            .filter((r) => r.block === "betrieb")
            .map((r) => r.ort)
            .filter((o): o is string => !!o),
        ),
      ).sort(),
    [allRows],
  );

  const rows = useMemo(
    () =>
      allRows.filter((row) => {
        if (ortFilter !== "alle" && row.ort !== ortFilter) return false;
        if (nurOhneVertrag && (row.versichert || row.quelle === "kpi")) return false;
        return true;
      }),
    [allRows, ortFilter, nurOhneVertrag],
  );

  // Die Matrix zeigt ausschließlich das Betriebsvermögen. Gebäude, PV und Kfz
  // laufen als Objektmappen im Abschnitt darunter — sie dürfen die Summe nicht
  // erhöhen.
  const betriebRows = useMemo(() => rows.filter((r) => r.block === "betrieb"), [rows]);
  const betriebAll = useMemo(() => allRows.filter((r) => r.block === "betrieb"), [allRows]);

  const ohneVertrag = useMemo(
    () => betriebAll.filter((r) => !r.versichert && r.quelle !== "kpi"),
    [betriebAll],
  );

  // Ungefilterte Zahl: der Abschnitt gilt als gefüllt, sobald überhaupt etwas
  // erfasst ist — ein Ortsfilter, der gerade nichts durchlässt, macht ihn nicht
  // leer.
  useEffect(() => {
    onBefund?.(betriebAll.length);
  }, [betriebAll.length, onBefund]);

  const firmCols = useMemo(
    () =>
      scopeFirmen.filter(
        (f) => selectedSet.has(f.id) && betriebRows.some((r) => r.ownerId === f.id),
      ),
    [scopeFirmen, selectedSet, betriebRows],
  );

  const sum = (list: RiskRow[]) => list.reduce((acc, r) => acc + r.wert, 0);

  const openRisk = (riskId: number) => navigate(`/risks/${riskId}`);

  const renderVersichertBadge = (list: RiskRow[]) => {
    // Vorratswerte aus den Kennzahlen haben keinen eigenen Vertrag — sie
    // gehören nicht in die Deckungsprüfung.
    const geprueft = list.filter((r) => r.quelle !== "kpi");
    if (geprueft.length === 0) return null;
    const offen = geprueft.filter((r) => !r.versichert);
    if (offen.length > 0)
      return (
        <span className={mergeClasses(styles.badge, styles.badgeDanger)}>
          ⚠ {eur(sum(offen))} ohne Vertrag
        </span>
      );
    return <span className={mergeClasses(styles.badge, styles.badgeOk)}>✓ versichert</span>;
  };

  /**
   * Versicherungsnehmer der Zeile. Fachlich zentral: bei Betriebsvermögen im
   * Unternehmensverbund ist der Eigentümer oft nicht der VN (Besitzgesellschaft
   * besitzt, Betreibergesellschaft versichert). Mehrere VN pro Vermögensart
   * sind normal, deshalb die Liste eindeutig gemacht statt „gemischt".
   */
  const renderVersicherte = (list: RiskRow[]) => {
    const namen = Array.from(
      new Set(list.filter((r) => r.versichert && r.vnName).map((r) => r.vnName as string)),
    );
    if (namen.length === 0) return <span className={styles.cellZero}>–</span>;
    return <span className={styles.vnList}>{namen.join(", ")}</span>;
  };

  /**
   * Zeilen der Matrix: IMMER der vollständige Kennzahlen-Katalog, auch wo nichts
   * erfasst ist. Die leeren Zeilen sind die Aussage — sie zeigen in der
   * Besprechung, was im Anlagenverzeichnis fehlt (Mietereinbauten,
   * Kundeneigentum, Geleastes). Positionen ohne Kennzahl hängen als eigene
   * Zeile an, damit die Summe vollständig bleibt.
   */
  const matrixZeilen = useMemo(() => {
    const zeilen = kennzahlZeilen();
    if (betriebRows.some((r) => r.kennzahl === OHNE_KENNZAHL))
      zeilen.push({
        knoten: {
          id: OHNE_KENNZAHL,
          label: kennzahlLabel(OHNE_KENNZAHL),
          hinweis: "Vermögensart ohne Entsprechung im Katalog — bitte die Kennzahl am Risiko setzen",
        },
        tiefe: 0,
        vorfahren: [],
        blaetter: [OHNE_KENNZAHL],
        istBlatt: true,
        traegtWert: true,
      } satisfies KennzahlZeile);
    return zeilen;
  }, [betriebRows]);

  const zeilenById = useMemo(
    () => new Map(matrixZeilen.map((z) => [z.knoten.id, z])),
    [matrixZeilen],
  );

  const drillRows = useMemo(() => {
    if (!drill) return null;
    const [id, sel] = drill.split("||");
    const zeile = zeilenById.get(id);
    if (!zeile) return null;
    const blaetter = new Set(zeile.blaetter);
    let hits = betriebRows.filter((r) => blaetter.has(r.kennzahl));
    let label = "gesamt";
    if (sel === "eigen" || sel === "fremd") {
      hits = hits.filter((r) => r.besitz === sel);
      label = sel === "eigen" ? "im Eigentum" : "im Besitz";
    } else if (sel?.startsWith("ort:")) {
      const ort = sel.slice(4);
      hits = hits.filter((r) => r.ort === ort);
      label = ort;
    } else if (sel?.startsWith("firm:")) {
      const fid = Number(sel.slice(5));
      hits = hits.filter((r) => r.ownerId === fid);
      label = firmNameById.get(fid) ?? `Kontakt ${fid}`;
    } else if (sel === "offen") {
      hits = hits.filter((r) => !r.versichert && r.quelle !== "kpi");
      label = "ohne Vertrag";
    }
    return hits.length ? { art: zeile.knoten.label, label, hits } : null;
  }, [drill, zeilenById, betriebRows, firmNameById]);

  const renderDrill = () =>
    drillRows && (
      <div className={styles.drill}>
        <div className={styles.drillHead}>
          <span style={{ color: BRAND.purple }}>{drillRows.art}</span>
          <span className={styles.muted}>· {drillRows.label}</span>
          <button className={styles.drillClose} onClick={() => setDrill(null)}>
            schließen ✕
          </button>
        </div>
        {drillRows.hits.map((row) => {
          const klickbar = row.quelle !== "kpi";
          return (
            <div
              key={row.risk.id}
              className={styles.drillItem}
              style={klickbar ? undefined : { cursor: "default" }}
              onClick={klickbar ? () => openRisk(row.risk.id) : undefined}
              role={klickbar ? "button" : undefined}
              tabIndex={klickbar ? 0 : undefined}
              onKeyDown={klickbar ? (e) => e.key === "Enter" && openRisk(row.risk.id) : undefined}
            >
              <strong>{eur(row.wert)}</strong>
              <span>{row.risk.name || row.risk.riskType || `Risiko ${row.risk.id}`}</span>
              {row.ort && <span className={styles.muted}>{row.ort}</span>}
              <span className={styles.muted}>Eigentümer: {row.ownerName}</span>
              {row.eigart && (
                <span className={mergeClasses(styles.badge, styles.badgeNeutral)}>{row.eigart}</span>
              )}
              {row.quelle === "kpi" ? (
                <span className={mergeClasses(styles.badge, styles.badgeNeutral)}>
                  aus den Kennzahlen
                </span>
              ) : row.versichert ? (
                <span className={mergeClasses(styles.badge, styles.badgeOk)}>
                  ✓ {row.sparte ?? "Vertrag"}
                  {row.contractNbr ? ` · ${row.contractNbr}` : ""}
                  {row.vnName ? ` · VN: ${row.vnName}` : ""}
                  {row.mitversicherte.length > 0
                    ? ` · mitversichert: ${row.mitversicherte.join(", ")}`
                    : ""}
                </span>
              ) : (
                <span className={mergeClasses(styles.badge, styles.badgeDanger)}>⚠ ohne Vertrag</span>
              )}
            </div>
          );
        })}
      </div>
    );

  /** Betragszelle mit Drill-Down; „–" bei leer. */
  const wertZelle = (
    key: string,
    wert: number,
    anzahl: number,
    extra?: string | false,
    stark = false,
  ) => (
    <td key={key} className={mergeClasses(styles.td, extra || undefined)}>
      {anzahl > 0 ? (
        <button
          className={mergeClasses(styles.cellBtn, styles.cellHas)}
          style={stark ? undefined : { fontWeight: 600 }}
          onClick={() => setDrill(drill === key ? null : key)}
        >
          {eur(wert)}
        </button>
      ) : (
        <span className={styles.cellZero}>–</span>
      )}
    </td>
  );

  const renderMatrix = () => {
    const shownOrte = ortFilter === "alle" ? orte : orte.filter((o) => o === ortFilter);
    const gesamt = summeFuer(
      betriebRows,
      matrixZeilen.filter((z) => z.traegtWert).map((z) => z.knoten.id),
    );
    return (
      <>
        <div className={styles.hint}>
          Versicherungssicht, nicht Buchhaltung: EDV zählt zur Elektronik, Mietereinbauten,
          Kundeneigentum und Geleastes gehören dazu, obwohl sie in keinem Anlagenverzeichnis
          stehen. Leere Zeilen sind bewusst sichtbar. Kunden- und Mitarbeitereigentum sowie
          Waren und Vorräte werden nicht in Eigentum / Besitz aufgeteilt — außer Handelswaren.
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={mergeClasses(styles.th, styles.thLeft)} rowSpan={2}>
                  Kennzahl
                </th>
                <th className={styles.th} rowSpan={2}>
                  Wert gesamt
                </th>
                <th
                  className={mergeClasses(styles.th, styles.thGroup, styles.groupBorder)}
                  colSpan={2}
                >
                  Davon
                </th>
                {shownOrte.length > 0 && (
                  <th
                    className={mergeClasses(styles.th, styles.thGroup, styles.groupBorder)}
                    colSpan={shownOrte.length}
                  >
                    Wo — Risikoorte
                  </th>
                )}
                {firmCols.length > 0 && (
                  <th
                    className={mergeClasses(styles.th, styles.thGroup, styles.groupBorder)}
                    colSpan={firmCols.length}
                  >
                    Wem — Firmen
                  </th>
                )}
                <th
                  className={mergeClasses(styles.th, styles.thLeft, styles.groupBorder)}
                  rowSpan={2}
                >
                  Versicherter (VN)
                </th>
                <th className={styles.th} rowSpan={2}>
                  Versichert
                </th>
              </tr>
              <tr>
                <th className={mergeClasses(styles.th, styles.groupBorder)}>
                  Im Eigentum
                  <InfoTipp text={ERKLAERUNG_EIGENTUM} label="Erklärung: Im Eigentum" />
                </th>
                <th className={styles.th}>
                  Im Besitz
                  <InfoTipp text={ERKLAERUNG_BESITZ} label="Erklärung: Im Besitz" />
                </th>
                {shownOrte.map((ort, i) => (
                  <th
                    key={ort}
                    className={mergeClasses(styles.th, i === 0 && styles.groupBorder)}
                    title={ort}
                  >
                    {ort.split(",")[0]}
                  </th>
                ))}
                {firmCols.map((f, i) => (
                  <th
                    key={f.id}
                    className={mergeClasses(styles.th, i === 0 && styles.groupBorder)}
                    title={f.name ?? undefined}
                  >
                    {(f.name ?? `Kontakt ${f.id}`).replace(/^kmvs-/, "")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrixZeilen
                .filter((z) => !z.vorfahren.some((v) => zugeklappt.has(v)))
                .map((z) => {
                const id = z.knoten.id;
                const blaetter = new Set(z.blaetter);
                const inZeile = betriebRows.filter((r) => blaetter.has(r.kennzahl));
                const s = summeFuer(betriebRows, z.blaetter);
                const gruppe = !z.istBlatt;
                const anzahl = (list: RiskRow[]) => list.length;
                return (
                  <tr
                    key={id}
                    className={gruppe && z.tiefe === 0 ? styles.kzGruppe : undefined}
                  >
                    <td
                      className={mergeClasses(
                        styles.td,
                        styles.tdLeft,
                        z.tiefe > 0 && styles.kzBlatt,
                        z.knoten.fett && styles.kzUntergruppe,
                      )}
                      style={{ paddingLeft: 6 + z.tiefe * 18 }}
                    >
                      {gruppe ? (
                        <button
                          type="button"
                          className={mergeClasses(
                            styles.kzToggle,
                            !zugeklappt.has(id) && styles.kzToggleOffen,
                          )}
                          aria-expanded={!zugeklappt.has(id)}
                          aria-label={`${z.knoten.label} ${zugeklappt.has(id) ? "aufklappen" : "einklappen"}`}
                          onClick={() => umklappen(id)}
                        >
                          <Icons.chevronRight />
                        </button>
                      ) : (
                        <span className={styles.kzTogglePlatz} />
                      )}
                      {z.knoten.label}
                      {z.knoten.hinweis && (
                        <InfoTipp text={z.knoten.hinweis} label={`Erklärung: ${z.knoten.label}`} />
                      )}
                    </td>
                    {wertZelle(`${id}||all`, s.gesamt, s.anzahl, false, true)}
                    {wertZelle(
                      `${id}||eigen`,
                      s.eigen,
                      inZeile.filter((r) => r.besitz === "eigen").length,
                      styles.groupBorder,
                    )}
                    {wertZelle(
                      `${id}||fremd`,
                      s.fremd,
                      inZeile.filter((r) => r.besitz === "fremd").length,
                    )}
                    {shownOrte.map((ort, i) => {
                      const cell = inZeile.filter((r) => r.ort === ort);
                      return wertZelle(
                        `${id}||ort:${ort}`,
                        sum(cell),
                        anzahl(cell),
                        i === 0 && styles.groupBorder,
                      );
                    })}
                    {firmCols.map((f, i) => {
                      const cell = inZeile.filter((r) => r.ownerId === f.id);
                      return wertZelle(
                        `${id}||firm:${f.id}`,
                        sum(cell),
                        anzahl(cell),
                        i === 0 && styles.groupBorder,
                      );
                    })}
                    <td
                      className={mergeClasses(styles.td, styles.tdLeft, styles.groupBorder)}
                    >
                      {renderVersicherte(inZeile)}
                    </td>
                    <td className={styles.td}>
                      <button
                        className={styles.cellBtn}
                        onClick={() => setDrill(drill === `${id}||offen` ? null : `${id}||offen`)}
                      >
                        {renderVersichertBadge(inZeile)}
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className={styles.totalRow}>
                <td className={mergeClasses(styles.td, styles.tdLeft)}>GESAMT</td>
                <td className={styles.td}>{eur(gesamt.gesamt)}</td>
                <td className={mergeClasses(styles.td, styles.groupBorder)}>{eur(gesamt.eigen)}</td>
                <td className={styles.td}>{eur(gesamt.fremd)}</td>
                {shownOrte.map((ort, i) => (
                  <td
                    key={ort}
                    className={mergeClasses(styles.td, i === 0 && styles.groupBorder)}
                  >
                    {eur(sum(betriebRows.filter((r) => r.ort === ort)))}
                  </td>
                ))}
                {firmCols.map((f, i) => (
                  <td
                    key={f.id}
                    className={mergeClasses(styles.td, i === 0 && styles.groupBorder)}
                  >
                    {eur(sum(betriebRows.filter((r) => r.ownerId === f.id)))}
                  </td>
                ))}
                <td className={mergeClasses(styles.td, styles.tdLeft, styles.groupBorder)}>
                  {renderVersicherte(betriebRows)}
                </td>
                <td className={styles.td}>{renderVersichertBadge(betriebRows)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {renderDrill()}
      </>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.scopeBar}>
        {/* Ebene + Firmenauswahl liegen seit SP2 in der Betriebsrisiko-Karte
            darüber — sie steuern mehrere Abschnitte, nicht nur die Matrix. */}
        <div className={styles.scopeRow}>
          <span className={styles.scopeLabel}>Filter</span>
          <select
            className={styles.select}
            value={ortFilter}
            onChange={(e) => {
              setOrtFilter(e.target.value);
              setDrill(null);
            }}
          >
            <option value="alle">Alle Risikoorte</option>
            {orte.map((ort) => (
              <option key={ort} value={ort}>
                {ort}
              </option>
            ))}
          </select>
          <button
            className={mergeClasses(styles.chip, nurOhneVertrag && styles.chipDanger)}
            onClick={() => {
              setNurOhneVertrag((v) => !v);
              setDrill(null);
            }}
          >
            ⚠ nur ohne Vertrag
            <span className={styles.chipCount}>{ohneVertrag.length}</span>
          </button>
        </div>
      </div>

      {/* Kennzahlen-Kacheln und die Blockkarten Gebäude/Photovoltaik/Kfz sind
          bewusst entfallen: Gebäude, PV und Kfz stehen als Objektmappen im
          Abschnitt darunter, die Summen stehen in der Matrix-Zeile GESAMT. */}

      {loading ? (
        <div className={styles.empty}>Risikoobjekte werden geladen …</div>
      ) : (
        renderMatrix()
      )}
    </div>
  );
}
